import * as FullStory from '@fullstory/browser';
import * as Sentry from '@sentry/browser';
import { getValueAndUnit } from 'polished';
import { ILabelValuePair, IError, ISelectOption, IValueOrNull } from './types';

// Retry function to help reduce the frequency of chunk load issues occurring and the instances recorded in Sentry.
export const retry = (fn: any, retriesLeft = 5, interval = 1000) =>
  new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: any) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error);
            return;
          }
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });

export function captureExceptionWithFullyStory(error: IError, extra?: Record<string, any>): void {
  if (!window.isHeadless) {
    Sentry.setTag('FS Session', FullStory.getCurrentSessionURL(true));
    if (error.status) {
      Sentry.setTag('error_status', error.status);
    }
    if (error.code) {
      Sentry.setTag('error_code', error.code);
    }
  }
  Sentry.captureException(
    error,
    extra && {
      extra,
    },
  );
}

export const isNextJS = !!process.env.NEXT_PUBLIC_API_HOST;
export const isOnServer = typeof window === 'undefined';

export const valueOrNull: IValueOrNull = (value = null) => value;

export const checkCurrency = (currencies: ISelectOption[], currency: string): string => {
  const formatCurrency = currency.toUpperCase().trim();
  const foundSupportCurrency = currencies.find((currencyList) => currencyList.value === formatCurrency);
  if (currency) {
    if (foundSupportCurrency) {
      return foundSupportCurrency.value!;
    }
  }
  return currencies[0].value!;
};

export const getCurrencySymbolOrName = (locale: string, currency: string, isSymbol = true): string =>
  (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency,
      currencyDisplay: isSymbol ? 'narrowSymbol' : 'name',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/[0-9\u0660-\u0669]/g, '')
    .trim();

export const loadCountries = (locale: any): Record<string, any> =>
  retry(() => import(`./countries/${locale}`).then((countries) => countries.default));

export const getCookie = (name: string): string | null => {
  if (!document.cookie) {
    return null;
  }

  const cookies = document.cookie
    .split(';')
    .map((c) => c.trim())
    .filter((c) => c.startsWith(`${name}=`));

  if (cookies.length === 0) {
    return null;
  }
  return decodeURIComponent(cookies[0].split('=')[1]);
};

export const multiply = (base = '1rem', multiplier = 1) => {
  switch (multiplier) {
    case 0:
      return 0;

    case 1:
      return base;

    default: {
      const [value, unit] = getValueAndUnit(base);
      return `${value * multiplier}${unit}`;
    }
  }
};

export const getReactSelectValue = (value: string, options: ILabelValuePair[]): ILabelValuePair | null =>
  // result cannot be undefined, otherwise react-select will show previous value
  options.filter((option) => option.value === value)?.[0] ?? null;
